import { useDeferredValue, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchBomMaterials } from '../api/client';

/**
 * 上传前「先选物料」弹窗（v0.85）。
 *
 * 沈哥 2026-10-22：
 *   「图纸必须归户，在分析之前就做归户，省得分析完后找不到归户，白做分析」
 *
 * ⇒ 上传流程从「上传 → 分析 → 事后归户」改成「**选物料 → 上传 → 分析**」。
 *   一页大图识别几十秒，挂错物料整套作废，所以物料必须在动手前定下来。
 *
 * 三条口径
 * --------
 * 1. **不能跳过**。没有「稍后再说」按钮 —— 那等于把老问题换个地方出现。
 * 2. **不能新建物料**。物料只来自 BOM；搜不到就提示去 BOM 建。
 * 3. **只列 P/B/D** 开头（后端已过滤，与零件主数据列表同一口径）。
 */
export function MaterialPickDialog({ fileName, onCancel, onPick }: {
  /** 待上传的文件名（让用户确认自己没选错文件）。 */
  fileName: string;
  onCancel: () => void;
  /** 选定物料 → 立刻发起上传。 */
  onPick: (materialCode: string, label: string) => void;
}) {
  const [q, setQ] = useState('');
  const kw = useDeferredValue(q);       // 输入不卡顿，查询用延迟值
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 40); }, []);

  const candQ = useQuery({
    queryKey: ['bom-materials', kw],
    queryFn: () => searchBomMaterials(kw, 40),
    refetchOnWindowFocus: false,
  });
  const items = candQ.data?.items ?? [];

  return (
    <>
      <div className="modal-backdrop" onClick={onCancel} />
      <div className="modal tree-dialog" role="dialog" aria-modal="true"
           style={{ width: 'min(680px, 94vw)' }}
           onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}>
        <div className="modal-title">第一步：这张图纸挂到哪个物料？</div>

        <div className="sip-state-desc" style={{ margin: '4px 0 10px' }}>
          待上传：<b>{fileName}</b><br />
          物料定了才开始识别 —— 识别一页要几十秒，挂错物料整套都得重来。
        </div>

        <input ref={inputRef} className="btn" value={q}
               style={{ width: '100%', textAlign: 'left', marginBottom: 8 }}
               placeholder="输入物料编码 / 客户零件号 / 名称（如 PC0960023）"
               onChange={(e) => setQ(e.target.value)} />

        {candQ.isLoading && <div className="sip-state">正在搜 BOM…</div>}

        <div style={{ maxHeight: '46vh', overflow: 'auto' }}>
          {items.map((m) => (
            <button key={m.material_code} disabled={busy}
                    onClick={() => {
                      setBusy(true);
                      onPick(m.material_code,
                             `${m.material_code}${m.material_name ? ` · ${m.material_name}` : ''}`);
                    }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '7px 10px', marginBottom: 3, borderRadius: 6,
                      border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer',
                    }}>
              <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>
                {m.material_code}
              </span>
              {m.material_name && <span> · {m.material_name}</span>}
              <span style={{ fontSize: 12, opacity: 0.65, marginLeft: 8 }}>
                {[m.specification, m.customer].filter(Boolean).join(' ｜ ')}
              </span>
            </button>
          ))}
          {!candQ.isLoading && items.length === 0 && (
            <div className="sip-state">
              <div className="sip-state-title">BOM 里搜不到「{q}」</div>
              <div className="sip-state-desc">
                物料由 BOM 平台维护 —— 先去 BOM 建好，等同步（每天 03:10，或手动跑
                <code> scripts/sync_bom.py</code>）之后再上传。
                本系统<b>不提供新建物料</b>。
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
          <button className="btn" onClick={onCancel} disabled={busy}>取消上传</button>
        </div>
      </div>
    </>
  );
}
